import { watchProject } from '@/data/Agents/watchProject'
import type { Eventually } from '@/data/clientUtils'
import { useEntityQuery } from '@/data/Entities/useEntityQuery'
import { useHubClient } from '@/data/Hubs/useHubClient'
import type { DefinedField } from '@/modules/Project/Fields/types'
import { computed } from 'vue'

export function useHubIndexing(props: {
  workspaceId: Eventually<string>
  hubId: Eventually<string>
}) {
  const { fetch: fetchHub } = useHubClient({ workspaceId: props.workspaceId })
  const { data: hub } = fetchHub(props.hubId)

  watchProject({ projectId: () => hub.value?.id })

  const { data: entity } = useEntityQuery({
    workspaceId: props.workspaceId,
    projectId: () => hub.value?.id,
    entityId: () => hub.value?.index?.entityId,
    staleTime: 5_000,
  })

  /** States that we interpret as non-computing */
  const stableStates: DefinedField['status'][] = ['complete', 'idle', 'error']

  /**
   * Indicates whether any field is currently processing.
   * True if any field has a non-stable status (not complete, idle, or error) or unknown staleness.
   */
  const isProcessing = computed(() => {
    const fields = Array.from(entity.value?.fields.values() ?? [])

    for (const field of fields) {
      if (!stableStates.includes(field.status)) {
        return true
      }

      if (field.staleness.status === 'unknown') {
        return true
      }
    }

    return false
  })

  /**
   * Indicates whether any field is actively indexing.
   * True if any field has status 'computing'.
   */
  const isIndexing = computed(() => {
    const fields = Array.from(entity.value?.fields.values() ?? [])
    return fields.some(({ status }) => status === 'computing')
  })

  /**
   * Indicates whether any field is stale.
   * True if any field's staleness status is not 'up_to_date'.
   */
  const isStale = computed(() => {
    const fields = Array.from(entity.value?.fields.values() ?? [])
    return fields.some(({ staleness }) => staleness.status !== 'up_to_date')
  })

  return { isStale, isProcessing, isIndexing }
}
